import React from 'react';
import { useHistory } from 'react-router-dom';

import { FormattedMessage } from '../../../util/reactIntl';
import {
  constructQueryParamName,
  parseSelectFilterOptions,
} from '../../../util/search';
import { stringify } from '../../../util/urlHelpers';

import css from './ActiveFilterChips.module.css';

/**
 * Resolve a query-param key + value to display chips.
 *
 * For an extended-data enum/multi-enum filter the value can be either
 * a single option (`pub_tilstand=som-ny`) or a CSV / `has_any:` /
 * `has_all:` list (`pub_materiale=ull,bomull`). We parse both and
 * return one chip per option, looking the label up via the filter's
 * enumOptions.
 *
 * Built-in filters (price range, dates, listing type, category) are
 * skipped — they're either driven by their own UI (slider, single
 * select) or live in the URL as multiple keys, which would clutter
 * the chip row. We can layer those in later if needed.
 */
const chipsForFilter = ({ filterConfig, urlQueryParams }) => {
  const { key, scope, schemaType, enumOptions = [], filterConfig: fc } = filterConfig;
  if (schemaType !== 'enum' && schemaType !== 'multi-enum') {
    return [];
  }

  const paramName = constructQueryParamName(key, scope);
  const raw = urlQueryParams?.[paramName];
  if (!raw) {
    return [];
  }

  // SelectMultipleFilter stores its value as CSV (or `has_any:`/`has_all:`
  // prefixed CSV) even when the underlying schemaType is plain `enum`.
  // Detect that and split so each option becomes its own chip.
  const isMulti =
    schemaType === 'multi-enum' ||
    fc?.filterType === 'SelectMultipleFilter' ||
    /^(has_any:|has_all:)/.test(raw) ||
    String(raw).includes(',');

  const values = isMulti ? parseSelectFilterOptions(raw) : [raw];

  return values
    .map(value => {
      const matched = enumOptions.find(o => String(o.option) === String(value));
      return {
        paramName,
        value,
        isMulti,
        label: matched?.label || value,
      };
    })
    .filter(Boolean);
};

/**
 * Row of "active filter" pills above the filter list. Each pill shows
 * the option's label with an × button that removes only that value
 * from the URL — the rest of the filter state stays as is. A "Reset
 * all" link sits at the end (or alone, if no chips), kept here so the
 * sidebar header has both affordances in one place.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.urlQueryParams – current parsed search params
 * @param {Array} props.availableFilters – relevant filter configs
 * @param {Function} [props.onResetAll] – optional reset-all click handler
 * @returns {JSX.Element|null}
 */
const ActiveFilterChips = props => {
  const { urlQueryParams, availableFilters, onResetAll } = props;
  const history = useHistory();

  const chips = (availableFilters || []).flatMap(filterConfig =>
    chipsForFilter({ filterConfig, urlQueryParams })
  );

  if (chips.length === 0 && !onResetAll) {
    return null;
  }

  const removeChip = ({ paramName, value, isMulti }) => {
    const next = { ...urlQueryParams };
    if (isMulti) {
      const rawVal = urlQueryParams[paramName];
      const prefixMatch = String(rawVal).match(/^(has_any:|has_all:)/);
      const prefix = prefixMatch ? prefixMatch[0] : '';
      const remaining = parseSelectFilterOptions(rawVal).filter(
        v => String(v) !== String(value)
      );
      if (remaining.length === 0) {
        delete next[paramName];
      } else {
        next[paramName] = `${prefix}${remaining.join(',')}`;
      }
    } else {
      delete next[paramName];
    }
    const search = stringify(next);
    history.push({ pathname: history.location.pathname, search: search ? `?${search}` : '' });
  };

  return (
    <div className={css.root}>
      {chips.map(chip => (
        <button
          key={`${chip.paramName}:${chip.value}`}
          type="button"
          className={css.chip}
          onClick={() => removeChip(chip)}
        >
          <span>{chip.label}</span>
          <span aria-hidden="true" className={css.chipClose}>
            ×
          </span>
          <span className={css.srOnly}>
            <FormattedMessage id="ActiveFilterChips.removeAriaLabel" />
          </span>
        </button>
      ))}
    </div>
  );
};

export default ActiveFilterChips;
